import { PageSize } from "@iventis/domain-model/model/pageSize";
import { PageOrientation } from "@iventis/domain-model/model/pageOrientation";
import { MapExportLegendPlacement } from "@iventis/domain-model/model/mapExportLegendPlacement";
import { Model } from "@iventis/domain-model/model/model";
import { MapExportNode } from "@iventis/domain-model/model/mapExportNode";
import { Node } from "@iventis/domain-model/model/node";
import { MapExportLayer } from "@iventis/domain-model/model/mapExportLayer";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { getModelLayerStyle } from "@iventis/map-engine/src/utilities/layer.helpers";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { getStaticStyleValueFromMapped } from "@iventis/layer-style-helpers";
import { isModelLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { getMostProminentStyleValue, getStaticAndMappedValues } from "@iventis/map-engine/src/utilities/style-helpers";
import { MapExportLayerDataDrivenStyleValue } from "@iventis/domain-model/model/mapExportLayerDataDrivenStyleValue";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import rhumbDestination from "@turf/rhumb-destination";
import { analysisLayerIds, commentsLayerId } from "@iventis/map-engine/src/bridge/constants";
import { LocalGeoJson } from "@iventis/map-types/src/map-object-types";
import { MapModuleLayer } from "@iventis/map-engine/src/types/store-schema";

/** Filter out all analysis and comment layers when using the export preview */
const LAYER_IDS_TO_FILTER = [...analysisLayerIds, commentsLayerId];

/** Width of the legend when export is landscape (px) */
const LANDSCAPE_LEGEND_WIDTH = 725;
/** Height of the legend when export is portrait (px) */
const PORTRAIT_LEGEND_HEIGHT = 725;
/** Width of the details section when export is landscape (px) */
const DETAILS_LANDSCAPE_WIDTH = 725;
/** Height of the details section when export is portrait (px) */
const DETAILS_PORTRAIT_HEIGHT = 820;
/** Amount of padding between map preview and it's container  */
const MAP_PADDING = 20;

export const PAGE_SIZES_FOR_RASTER = [PageSize.A4, PageSize.A3, PageSize.A2, PageSize.Tabloid, PageSize.Letter, PageSize.Legal];

export const ALL_PAGE_SIZES = [
    { id: PageSize.A4, name: "A4" },
    { id: PageSize.A3, name: "A3" },
    { id: PageSize.A2, name: "A2" },
    { id: PageSize.A1, name: "A1" },
    { id: PageSize.A0, name: "A0" },
    { id: PageSize.Tabloid, name: "Tabloid" },
    { id: PageSize.Letter, name: "Letter" },
    { id: PageSize.Legal, name: "Legal" },
];

/** Get flex direction for map preview container (positions where the legend goes) */
export const getFlexDirection = (legendPosition: MapExportLegendPlacement): "row" | "column" | "column-reverse" | "row-reverse" => {
    switch (legendPosition) {
        case MapExportLegendPlacement.Top:
            return "column";
        case MapExportLegendPlacement.Bottom:
            return "column-reverse";
        case MapExportLegendPlacement.Left:
            return "row";
        case MapExportLegendPlacement.Right:
            return "row-reverse";
        default:
            return "row";
    }
};

/** Get width and height of the legend */
export const getLegendDimensions = (pageSize: PageSize, orientation: PageOrientation, mapElementWidth: number, mapElementHeight: number): { width: number; height: number } => {
    if (mapElementWidth == null || mapElementHeight == null) {
        return { width: 0, height: 0 };
    }

    const mapDimension = mapDimensions.find((mapDimension) => mapDimension.pageSize === pageSize && mapDimension.orientation === orientation);

    if (mapDimension == null) {
        return { width: 0, height: 0 };
    }

    if (orientation === PageOrientation.Landscape) {
        // Remove the legend width
        const mapWidth = mapDimension.width - LANDSCAPE_LEGEND_WIDTH;
        const legendWidth = mapDimension.width - mapWidth;

        // Get ratio of the map getting exported to preview map
        const ratio = mapElementWidth / mapDimension.width;
        const legendRealWidth = legendWidth * ratio;
        return { width: legendRealWidth, height: mapElementHeight };
    }

    if (orientation === PageOrientation.Portrait) {
        // Remove legend height
        const mapHeight = mapDimension.height - PORTRAIT_LEGEND_HEIGHT;
        const legendHeight = mapDimension.height - mapHeight;

        // Get ratio of the map getting exported to preview map
        const ratio = mapElementHeight / mapDimension.height;
        const legendRealHeight = legendHeight * ratio;
        return { width: mapElementWidth, height: legendRealHeight };
    }

    return { width: 0, height: 0 };
};

/** Get the width and height of the eventual exported map */
export const getExportedMapHeightAndWidth = (orientation: PageOrientation, pageSize: PageSize, legend: boolean): { height: number; width: number } => {
    const mapDimension = mapDimensions.find((dimension) => dimension.orientation === orientation && dimension.pageSize === pageSize);

    if (mapDimension == null) {
        return { height: 0, width: 0 };
    }

    const margin = 84; // 40 pixels of margin and 2 pixels of border on each side

    if (!legend) {
        return { height: mapDimension.height - margin, width: mapDimension.width - margin };
    }

    if (orientation === PageOrientation.Landscape) {
        // The map width should deduct the legend width of 725 and 2 additional borders of 2px each
        return { height: mapDimension.height - margin, width: mapDimension.width - LANDSCAPE_LEGEND_WIDTH - margin - 4 };
    }

    // The map height should deduct the legend height of 725 and 2 additional borders of 2px each
    if (orientation === PageOrientation.Portrait) {
        return { height: mapDimension.height - PORTRAIT_LEGEND_HEIGHT - margin, width: mapDimension.width - margin - 4 };
    }

    return { height: 0, width: 0 };
};

/** Get width and height of the preview map */
export const getMapHeightAndWidth = (orientation: PageOrientation, pageSize: PageSize, legend: boolean, containerHeight: number): { height: number; width: number } => {
    if (containerHeight == null) {
        return { height: 0, width: 0 };
    }

    const mapDimension = mapDimensions.find((dimension) => dimension.orientation === orientation && dimension.pageSize === pageSize);

    if (mapDimension == null) {
        return { height: 0, width: 0 };
    }

    // Get ratio of the map getting exported to preview map
    const ratio = (containerHeight - MAP_PADDING) / mapDimension.height;

    if (!legend) {
        return { height: Math.round(mapDimension.height * ratio), width: Math.round(mapDimension.width * ratio) };
    }

    if (orientation === PageOrientation.Landscape) {
        return { height: Math.round(mapDimension.height * ratio), width: Math.round((mapDimension.width - LANDSCAPE_LEGEND_WIDTH) * ratio) };
    }

    if (orientation === PageOrientation.Portrait) {
        return { height: Math.round((mapDimension.height - PORTRAIT_LEGEND_HEIGHT) * ratio), width: Math.round(mapDimension.width * ratio) };
    }

    return { height: 0, width: 0 };
};

/** Get width and height (as a percentage) for the details section inside the legend */
export const getDetailDimension = (orientation: PageOrientation, pageSize: PageSize) => {
    const mapDimension = mapDimensions.find((dimension) => dimension.orientation === orientation && pageSize === dimension.pageSize);

    if (mapDimension == null) {
        return { height: 0, width: 0 };
    }

    if (orientation === PageOrientation.Portrait) {
        return { width: (DETAILS_LANDSCAPE_WIDTH / mapDimension.width) * 100, height: 100 };
    }

    if (orientation === PageOrientation.Landscape) {
        return { width: 100, height: (DETAILS_PORTRAIT_HEIGHT / mapDimension.height) * 100 };
    }

    return { height: 0, width: 0 };
};

/** Gets all the models from the layer styles and gets the models asset */
export const getUniqueModels = async (layers: MapLayer[], multipleModelsGetter: (ids: string[]) => Promise<Model[]>) => {
    // Get all model layers
    const modelLayers = layers.filter((layer) => isModelLayer(layer));
    // For each layer get their model style
    const modelLayerStyleValues = modelLayers.map((layer) => getModelLayerStyle(layer).model);
    const uniqueModelIds = modelLayerStyleValues.reduce((uniqueModelIds, modelStyleValue) => {
        const modelIds = getStaticAndMappedValues(modelStyleValue);
        // Ensure there are no duplicate model ids
        modelIds.forEach((modelId) => {
            if (!uniqueModelIds.some((id) => id === modelId)) {
                uniqueModelIds.push(modelId);
            }
            return uniqueModelIds;
        });
        return uniqueModelIds;
    }, []);
    // Request all the model assets in a single request
    const models = await multipleModelsGetter(uniqueModelIds);
    return models;
};

export const parseNodesToExportMapNodes = (nodes: Node[]): MapExportNode[] => {
    const filteredNodes = nodes.filter((node) => node.type === NodeType.Layer || node.type === NodeType.Folder);

    const parseNode = (node: Node): MapExportNode => ({
        id: node.id,
        name: node.name,
        parentId: node.parentId,
        childNodes: node.childNodes.filter((node) => node.type === NodeType.Layer || node.type === NodeType.Folder).map((childNode) => parseNode(childNode)),
    });

    return filteredNodes.map((node) => parseNode(node));
};

export const parseLayerToExportMapLayers = (layers: MapLayer[], layerToDataDrivenStyleItems: { [layerId: string]: MapExportLayerDataDrivenStyleValue[] }): MapExportLayer[] =>
    layers.map((layer) => ({
        id: layer.id,
        name: layer.name,
        sidebarOrder: layer.sidebarOrder,
        modelId: layer.styleType === StyleType.Model || layer.styleType === StyleType.LineModel ? getStaticStyleValueFromMapped(getModelLayerStyle(layer).model) : null,
        dataDrivenStyleValues: layerToDataDrivenStyleItems[layer.id] ?? [],
    }));

/** Assigns each layer the list items which affect their most prominent style
 *  - Point, Line, Area => "colour";
 *  - Icon => "iconImage";
 *  - Model, LineModel => "model");
 */
export const getAllAttributeListItemsAffectingLayerStyle = async (layers: MapLayer[], listItemGetter: (attributeId: string) => Promise<DataFieldListItem[]>) => {
    // get each layers most prominent style value and narrow to ones which include mapped data driven styling
    const record: { [layerId: string]: { attributeId: string; listItemsAffectingStyle: string[] } } = {};
    layers.forEach((layer) => {
        const styleValue = getMostProminentStyleValue(layer);
        if (styleValue.extractionMethod !== StyleValueExtractionMethod.Mapped || styleValue.dataFieldId == null || Object.keys(styleValue.mappedValues ?? {})?.length === 0) {
            return;
        }
        record[layer.id] = { attributeId: styleValue.dataFieldId, listItemsAffectingStyle: Object.keys(styleValue.mappedValues) };
    });

    // Fetch the full list items
    const uniqueueAttributeIds = Object.values(record).reduce((acc, record) => (acc.includes(record.attributeId) ? acc : [...acc, record.attributeId]), []);
    const attributeToListItemsRecord: Record<string, DataFieldListItem[]> = {};
    await Promise.all(
        uniqueueAttributeIds.map(async (attributeId) => {
            const values = await listItemGetter(attributeId);
            attributeToListItemsRecord[attributeId] = values;
        })
    );

    // Assign layers to their style driving list items
    return Object.entries(record).reduce<{ [layerId: string]: MapExportLayerDataDrivenStyleValue[] }>(
        (acc, [layerId, data]) => ({
            ...acc,
            [layerId]: attributeToListItemsRecord[data.attributeId].filter((l) => data.listItemsAffectingStyle.includes(l.id)),
        }),
        {}
    );
};

/** Height of legend items, makes them smaller as page size increases */
export const getSidebarLegendItemHeight = (pageSize: PageSize) => {
    switch (pageSize) {
        case PageSize.A4:
        case PageSize.Letter:
            return 5;
        case PageSize.A3:
        case PageSize.A2:
        case PageSize.Tabloid:
        case PageSize.Legal:
            return 4;
        case PageSize.A1:
        case PageSize.A0:
            return 3;
        default:
            return 0;
    }
};

/** Width of legend items, makes them smaller as page size increases */
export const getSidebarLegendItemWidth = (pageSize: PageSize) => {
    switch (pageSize) {
        case PageSize.A4:
        case PageSize.Letter:
            return 20;
        case PageSize.A3:
        case PageSize.A2:
        case PageSize.Tabloid:
        case PageSize.Legal:
            return 15;
        case PageSize.A1:
        case PageSize.A0:
            return 10;
        default:
            return 5;
    }
};

/** Returns the effective layer visibility, ignoring any local visibility changes. */
export const getLayerVisibilityForExport = (layer: Pick<MapModuleLayer, "id" | "visible">, layersWithTemporaryVisibilityValue: string[]): boolean => {
    if (layersWithTemporaryVisibilityValue.includes(layer.id)) {
        return !layer.visible;
    }
    return layer.visible;
};

/** Export map dimensions for each combination of page size and page  orientation */
const mapDimensions = [
    { pageSize: PageSize.A4, orientation: PageOrientation.Landscape, width: 3510, height: 2490 },
    { pageSize: PageSize.A4, orientation: PageOrientation.Portrait, width: 2490, height: 3510 },
    { pageSize: PageSize.A3, orientation: PageOrientation.Landscape, width: 4950, height: 3510 },
    { pageSize: PageSize.A3, orientation: PageOrientation.Portrait, width: 3510, height: 4950 },
    { pageSize: PageSize.A2, orientation: PageOrientation.Landscape, width: 7020, height: 4950 },
    { pageSize: PageSize.A2, orientation: PageOrientation.Portrait, width: 4950, height: 7020 },
    { pageSize: PageSize.A1, orientation: PageOrientation.Landscape, width: 9930, height: 7020 },
    { pageSize: PageSize.A1, orientation: PageOrientation.Portrait, width: 7020, height: 9930 },
    { pageSize: PageSize.A0, orientation: PageOrientation.Landscape, width: 14040, height: 9930 },
    { pageSize: PageSize.A0, orientation: PageOrientation.Portrait, width: 9930, height: 14040 },
    { pageSize: PageSize.Tabloid, orientation: PageOrientation.Landscape, width: 5100, height: 3300 },
    { pageSize: PageSize.Tabloid, orientation: PageOrientation.Portrait, width: 3300, height: 5100 },
    { pageSize: PageSize.Letter, orientation: PageOrientation.Landscape, width: 3300, height: 2550 },
    { pageSize: PageSize.Letter, orientation: PageOrientation.Portrait, width: 2550, height: 3300 },
    { pageSize: PageSize.Legal, orientation: PageOrientation.Landscape, width: 4200, height: 2550 },
    { pageSize: PageSize.Legal, orientation: PageOrientation.Portrait, width: 2550, height: 4200 },
];

/** Normalise a bearing to be between 0 and 180 degrees */
export const normaliseBearing = (bearing: number) => {
    if (bearing < 0) {
        return bearing + 360;
    }
    if (bearing >= 360) {
        return bearing - 360;
    }
    return bearing;
};

/** Gets the southwest and northeast corner of a bounding box, centred on a specific position and to scale */
export const getScaledBounds = (scale: number, pixelsWidth: number, pixelsHeight: number, centre: { lng: number; lat: number }, dpi: number): [number, number, number, number] => {
    const pixelsHalfWidth = pixelsWidth / 2;
    const pixelsHalfHeight = pixelsHeight / 2;

    // Use pythagoras to calculate the diagonal distance from the centre to the corner
    const pixelsCentreToCorner = Math.sqrt(pixelsHalfWidth * pixelsHalfWidth + pixelsHalfHeight * pixelsHalfHeight);

    // Convert to inches so we know how much space the canvas can cover at 1:1 scale
    const inchesCentreToCorner = pixelsCentreToCorner / dpi;

    // Multiply by the scale to get the real world distance in miles from centre to corner
    const inchesScaledCentreToCorner = inchesCentreToCorner * scale;

    // Calculate the bearing of the line to be drawn from the centre to each corner
    const bearingNorthEast = normaliseBearing((Math.asin(pixelsHalfWidth / pixelsCentreToCorner) * 180) / Math.PI);
    const bearingSouthWest = bearingNorthEast + 180;

    // Calculate the coordinates of the 4 corners using the turf rhumbDestination function
    const cornerNorthEast = rhumbDestination([centre.lng, centre.lat], inchesScaledCentreToCorner, bearingNorthEast, { units: "inches" });
    const cornerSouthWest = rhumbDestination([centre.lng, centre.lat], inchesScaledCentreToCorner, bearingSouthWest, { units: "inches" });

    return [cornerSouthWest.geometry.coordinates[0], cornerSouthWest.geometry.coordinates[1], cornerNorthEast.geometry.coordinates[0], cornerNorthEast.geometry.coordinates[1]];
};
/** Removes comment and analysis layers */
export function filterLayersForMapExport(layers: MapModuleLayer[]) {
    if (layers == null) {
        return [];
    }

    return layers.filter((layer) => !LAYER_IDS_TO_FILTER.includes(layer.id));
}

/** Removes comment and analysis map objects */
export function filterMapObjectsForMapExport(geojson: LocalGeoJson) {
    if (geojson == null) {
        return {};
    }

    const filteredGeoJSON: LocalGeoJson = {};
    Object.keys(geojson).forEach((key) => {
        if (!LAYER_IDS_TO_FILTER.includes(key)) {
            filteredGeoJSON[key] = geojson[key];
        }
    });
    return filteredGeoJSON;
}
